import PropTypes from 'prop-types';
import React from 'react';
import { Box, Grid, useMediaQuery, InputLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LabelChip from './LabelChip';

/*
This component is to wrap any smaller components so that it is aligned with the the form format.
*/
const FieldWrapper = ({
  doubleAsterisk,
  children,
  hideRequiredAsterisk,
  label,
  required,
  md,
  sm,
  xs,
  alignItems,
  showLabelChip,
  labelChipNotes,
  labelPlacement = 'side',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Label breakpoints cannot be 0
  const labelBreakPoints = {
    md: md !== 12 ? 12 - md : 3,
    sm: sm !== 12 ? 12 - sm : 3,
  };

  const LabelNode = (
    <InputLabel required={required && !hideRequiredAsterisk}>
      {label}
      {/* Render an additional custom asterisk if required and we want a double asterisk */}
      {required && !hideRequiredAsterisk && doubleAsterisk && (
      <span style={{ color: theme.palette.error.dark }}>
                    &thinsp;*
      </span>
      )}
    </InputLabel>
  );

  return (
    <Box mb={1}>
      <Grid container alignItems={alignItems || 'center'}>
        {/* for label that is placed on top */}
        {labelPlacement === 'top' ? (label && (
          <Grid
            size={{
              md: labelPlacement === 'top' ? 12 : md,
              sm: labelPlacement === 'top' ? 12 : sm,
              xs: labelPlacement === 'top' ? 12 : xs,
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.3,
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              mb: 0.5,
            }}
            >
              {LabelNode}
              {showLabelChip && labelChipNotes && <LabelChip notes={labelChipNotes} />}
            </Box>
          </Grid>
        )) : (
          label && (
          <Grid
            size={{
              md: labelBreakPoints.md,
              sm: labelBreakPoints.sm,
              xs: 12,
            }}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: isMobile ? 'flex-start' : 'flex-end', // left on mobile, right on desktop
              alignItems: 'baseline',
              gap: 0.3, // space between label and chip
              flexWrap: { xs: 'wrap', sm: 'nowrap' }, // wrap only on mobile
              marginBottom: 0.5,
            }}
            >
              {LabelNode}
            </Box>
          </Grid>
          )
        )}
        {/* Label Component */}

        <Grid
          size={{
            md: labelPlacement === 'top' ? 12 : md,
            sm: labelPlacement === 'top' ? 12 : sm,
            xs: labelPlacement === 'top' ? 12 : xs,
          }}
        >
          {/* Input Component */}
          {children}
        </Grid>
      </Grid>
    </Box>
  );
};

FieldWrapper.propTypes = {
  doubleAsterisk: PropTypes.bool,
  children: PropTypes.node,
  hideRequiredAsterisk: PropTypes.bool,
  label: PropTypes.string,
  required: PropTypes.bool,
  md: PropTypes.number,
  sm: PropTypes.number,
  xs: PropTypes.number,
  alignItems: PropTypes.string,
  labelChipNotes: PropTypes.string,
  showLabelChip: PropTypes.bool,
  labelPlacement: PropTypes.oneOf(['top', 'left']),
};

FieldWrapper.defaultProps = {
  doubleAsterisk: false,
  hideRequiredAsterisk: false,
  required: false,
  md: 9,
  sm: 9,
  xs: 12,
};

export default FieldWrapper;
